import React, { useState, useEffect} from "react";
import { TextField, IconButton, InputAdornment, Typography } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Cta } from '../components/Cta.js';
import { ReactComponent as Check } from "../assets/loginPage/check.svg";
import { ReactComponent as Briefcase } from "../assets/loginPage/briefcase.svg";
import { ReactComponent as Group31 } from "../assets/loginPage/group-31.svg";
import maskGroup from "../assets/loginPage/mask-group.png";
import { ReactComponent as Profile } from "../assets/loginPage/profile.svg";
import "../css/LogPage.css";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, sendEmailVerification, applyActionCode, getAuth, reload } from "firebase/auth";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { StyledEngineProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";

/* Login should be done, but it could use a QA check for bugs.  */

const LogPage = () => {
    const [verificationAttempts, setVerificationAttempts] = useState(0);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const auth = getAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const BASE_URL = window.location.hostname === 'localhost'
        ? 'http://localhost:3000'
        : 'https://joynet-99b18.web.app';
    
    const API_BASE_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:5001'
    : 'https://us-central1-joynet-99b18.cloudfunctions.net/app';

    const sendTokenToServer = async () => {
        try {
            if (!auth.currentUser) {
                throw new Error('No user is signed in');
            }

            await auth.currentUser.reload(); // 🔁 Refresh user data before token

            const token = await auth.currentUser.getIdToken();
            
            const response = await fetch(`${API_BASE_URL}/api/verify-token`, { 
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();
            
            if (!data.success) {
                throw new Error(data.message);
            }
            // createPopup("Token true success!", "success"); -> debug

            return true;

        } catch (error) {
            // createPopup("Authentication error. Do you have an account?", 'error')
            createPopup(error.message || "An unexpected error occurred.", 'error') // -> debug
            return false;
        }
    };

    const actionCodeSettings = {
        // URL you want to redirect back to. The domain (www.example.com) for this
        // URL must be in the authorized domains list in the Firebase Console.
        // todo correct this to the production url
        url: `${BASE_URL}/login`,
        handleCodeInApp: false,
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {

            const userCredential = await signInWithEmailAndPassword(
                auth, 
                formData.email, 
                formData.password
            );
            
            await reload(userCredential.user);
            // Check if email is verified
            if (!userCredential.user.emailVerified) {
                
                if (verificationAttempts >= 3) {
                    createPopup("Too many verification emails. Kindly try again later.", 'error');
                    return;
                }
                await sendEmailVerification(userCredential.user, actionCodeSettings).then(() => {
                    createPopup("Your email is not verified. Check your inbox.", 'error');
                }).catch((error) => {
                    console.error("Error sending verification email:", error);
                    createPopup("Failed to send verification email.", 'error');
                });
                return;
            }
            
            const verified = await sendTokenToServer();
            if (verified) {
                const userDocRef = doc(db, "UserInformation", userCredential.user.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    createPopup("Welcome!", "success");
                    navigate('/'); // Navigate to the home page if user data exists
                } else {
                    createPopup("One More step!", "success");
                    navigate('/justonemorestep'); // Navigate to the next step if user data does not exist
                }
            } 
        } catch (error) {
            console.error('Sign in error:', error);
            createPopup("Invalid email or password.", 'error')
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const createPopup = (messageText, type = 'success') => {
        const popup = document.createElement('div');
        popup.className = `popup ${type}`;
        
        const closeBtn = document.createElement('button');
        closeBtn.className = 'popup-close';
        closeBtn.innerHTML = '×';
        closeBtn.onclick = () => popup.remove();
        
        const message = document.createElement('div');
        message.className = 'popup-message';
        message.innerHTML = messageText;
        
        popup.appendChild(closeBtn);
        popup.appendChild(message);
        document.body.appendChild(popup);

        setTimeout(() => {
            popup.remove();
        }, 2000);
    };

    useEffect(() => {
        const verifiedParam = searchParams.get("verified");

        if (verifiedParam === "true") {
            createPopup("Email successfully verified!", "success");
        } else if (verifiedParam === "false") {
            createPopup("Verification failed. Attempt login to receive another link!", "error");
        }

        // Remove the query param from URL after showing
        if (verifiedParam) {
            const url = new URL(window.location);
            url.searchParams.delete("verified");
            window.history.replaceState({}, '', url);
        }
    }, []);

    return (
    <StyledEngineProvider injectFirst>
    <CssBaseline />
    <div className="auth-page">
        <div className="left-panel">
            <div className="logo-container">
                <Group31 className="group responsive-img" />
                <div>
                    <span className="joynet-text">JoyNet</span>
                </div>
            </div>

            <Typography variant="subtitle2" color="primary" className="tagline">
                WELCOME TO YOUR SPACE OF HEALING AND JOY
            </Typography>
            <Typography variant="h4" className="title-text">
                At JoyNet, Every Heart Finds a Journey to Joy
            </Typography>
            <Box
                component="form"
                className="signup-form"
                onSubmit={handleSubmit}
            >
                <TextField
                    name="email"
                    label="Email"
                    variant="filled"
                    value={formData.email}
                    onChange={handleChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Profile className="input-icon" />
                            </InputAdornment>
                        ),
                        disableUnderline: true
                    }}
                />

                <TextField
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="filled"
                    value={formData.password}
                    onChange={handleChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Briefcase className="input-icon" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        disableUnderline: true
                    }}
                />

                <Cta text="Log In" type="submit" onClick={handleSubmit} />

                <Typography variant="body2" className="sign-up-text">
                    Don't already have an account?&nbsp;
                    <span className="login-link" onClick={() => navigate('/signup')}>
                        Sign Up
                    </span>
                </Typography>
            </Box>
        </div>
            
        <div className="right-panel">
            <div className="benefit-card">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Check className="benefit-icon" />
                    <div>
                        <div className="benefit-title">Begin Your Journey to Healing</div>
                        <div className="benefit-description">
                            Find solace and connection in our community. The JoyNet App is a sanctuary where conversations heal and uplift.
                        </div>
                    </div>
                </div>
            </div>

            <div className="benefit-card">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Check className="benefit-icon" />
                    <div>
                        <div className="benefit-title">Simplicity in Connection, Richness in Experience</div>
                        <div className="benefit-description">
                            Signing up is just the start. Join chatrooms, connect with kindred spirits, and share in the celebration of Black Joy.
                        </div>
                    </div>
                </div>
            </div>
            <img src={maskGroup} alt="Illustration" className="auth-image" />
        </div>
    </div>
    </StyledEngineProvider>
    );
};

export default LogPage;